/** Tiny SQL literal helpers for the seed generator. */
export type SqlValue = string | number | boolean | null | undefined | Date | { json: unknown } | { raw: string };

export function lit(v: SqlValue): string {
  if (v === null || v === undefined) return "NULL";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "NULL";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (v instanceof Date) return `'${v.toISOString()}'::timestamptz`;
  if (typeof v === "object" && "json" in v) return `${quote(JSON.stringify(v.json))}::json`;
  if (typeof v === "object" && "raw" in v) return v.raw;
  return quote(v);
}

export function quote(s: string): string {
  return `'${s.replace(/'/g, "''")}'`;
}

export function values(rows: SqlValue[][]): string {
  return rows.map((r) => `(${r.map(lit).join(", ")})`).join(",\n    ");
}

/** A dollar-quoted plpgsql DO block. */
export function doBlock(declare: string[], body: string): string {
  return `DO $$\nDECLARE\n${declare.map((d) => `  ${d};`).join("\n")}\nBEGIN\n${body}\nEND $$;`;
}

/**
 * **One `DO` block is one SQL statement, and a statement has a ceiling.** The
 * AWS RDS Data API refuses anything over 65,536 characters — *"Number of
 * characters in SQLStatement is longer than 65536"* — which `04`, `05` and above
 * all `06` (2 MB) sail past as a single block. `psql` does not care; the Data
 * API is how the demo database is actually reached (Pete, 2026-09-22).
 *
 * So a file is emitted as several blocks instead of one. Each repeats the
 * `preamble` — the org and creator lookups every statement needs, because a
 * `DECLARE` is scoped to its own block — and then carries as many `units` as fit
 * under the budget. A **unit is whatever must not be split**: one patient with
 * all of its visits, one provider with all of its shifts. Splitting inside one
 * would separate a `pt_id := …` from the inserts that use it.
 *
 * The budget is deliberately short of the ceiling: the preamble, the `DECLARE`
 * and the wrapper are counted, and a few thousand characters of headroom cost
 * nothing while a file that is one character over costs a rehearsal.
 */
export const STATEMENT_BUDGET = 55_000;

export function doBlocks(declare: string[], preamble: string[], units: string[]): string[] {
  const head = preamble.join("\n");
  const overhead = head.length + declare.join(";\n").length + 64;
  const out: string[] = [];
  let batch: string[] = [];
  const flush = () => {
    if (batch.length) out.push(doBlock(declare, [head, ...batch].join("\n")));
    batch = [];
  };
  let size = 0;
  for (const unit of units) {
    // A unit that cannot fit on its own is a bug in whatever built it, not
    // something to paper over by emitting a statement that will be refused.
    if (unit.length + overhead > STATEMENT_BUDGET) throw new Error(`seed unit of ${unit.length} chars exceeds the ${STATEMENT_BUDGET}-character statement budget; split it where it is built`);
    if (size + unit.length + overhead > STATEMENT_BUDGET) {
      flush();
      size = 0;
    }
    batch.push(unit);
    size += unit.length + 1;
  }
  flush();
  return out;
}

export const ORG_NAME = "Home Health Demo";
export const ORG_UNIT_NAME = "Seattle";
export const SERVICE_LINE_NAME = "Home Health";
export const SHIFT_CENTER_NAME = "Seattle branch";
export const CREATOR_EMAIL_DEFAULT = "pete@medarrive.com";
/**
 * The service that carries the tenant's demo day in its description. Attached to
 * no visit type and inactive, so the product never shows it; returned by
 * `OrgConfig` at the root, so the demo always sees it.
 */
export const DEMO_DAY_SERVICE = "Demo day";

/** Subqueries resolving natural keys → ids inside the Home Health Demo org. */
export const q = {
  org: `(SELECT organization_id FROM marlin.organization WHERE name = ${quote(ORG_NAME)} AND deleted = false)`,
  creator: (email: string) => `(SELECT user_id FROM marlin."user" WHERE email = ${quote(email)} AND deleted = false ORDER BY user_id LIMIT 1)`,
  orgUnit: `(SELECT organizational_unit_id FROM marlin.organizational_unit WHERE name = ${quote(ORG_UNIT_NAME)} AND deleted = false AND organization_id = org_id)`,
  serviceLine: `(SELECT service_line_id FROM marlin.service_line WHERE name = ${quote(SERVICE_LINE_NAME)} AND deleted = false AND organization_id = org_id)`,
  shiftCenter: `(SELECT shift_center_id FROM marlin.shift_center WHERE name = ${quote(SHIFT_CENTER_NAME)} AND deleted = false AND organization_id = org_id)`,
  providerType: (name: string) => `(SELECT provider_type_id FROM marlin.provider_type WHERE name = ${quote(name)} AND deleted = false AND organization_id = org_id)`,
  visitType: (name: string) => `(SELECT visit_type_id FROM marlin.visit_type WHERE name = ${quote(name)} AND deleted = false AND organization_id = org_id)`,
  requirement: (visitTypeName: string) => `(SELECT visit_resource_requirement_id FROM marlin.visit_resource_requirement r WHERE r.visit_type_id = ${`(SELECT visit_type_id FROM marlin.visit_type WHERE name = ${quote(visitTypeName)} AND deleted = false AND organization_id = org_id)`} AND r.deleted = false ORDER BY r.visit_resource_requirement_id LIMIT 1)`,
  userByEmail: (email: string) => `(SELECT user_id FROM marlin."user" WHERE email = ${quote(email)} AND deleted = false ORDER BY user_id LIMIT 1)`,
  orgUserByEmail: (email: string) => `(SELECT ou.organization_user_id FROM marlin.organization_user ou JOIN marlin."user" u ON u.user_id = ou.user_id WHERE u.email = ${quote(email)} AND ou.organization_id = org_id AND ou.deleted = false ORDER BY ou.organization_user_id LIMIT 1)`,
  providerByEmail: (email: string) => `(SELECT p.provider_id FROM marlin.provider p JOIN marlin.organization_user ou ON ou.organization_user_id = p.organization_user_id JOIN marlin."user" u ON u.user_id = ou.user_id WHERE u.email = ${quote(email)} AND p.organization_id = org_id AND p.deleted = false ORDER BY p.provider_id LIMIT 1)`,
  patientByName: (first: string, last: string) => `(SELECT patient_id FROM marlin.patient WHERE first_name = ${quote(first)} AND last_name = ${quote(last)} AND organization_id = org_id AND deleted = false ORDER BY patient_id LIMIT 1)`,
  episodeByPatient: (first: string, last: string) => `(SELECT e.episode_id FROM marlin.episode e WHERE e.patient_id = (SELECT patient_id FROM marlin.patient WHERE first_name = ${quote(first)} AND last_name = ${quote(last)} AND organization_id = org_id AND deleted = false ORDER BY patient_id LIMIT 1) AND e.deleted = false AND e.active = true ORDER BY e.episode_id LIMIT 1)`,
  cancellationReason: (description: string) => `(SELECT visit_cancellation_reason_id FROM marlin.visit_cancellation_reason WHERE description = ${quote(description)} AND deleted = false AND organization_id = org_id)`,
};
